import React, { Fragment, useState, type JSX } from 'react';
import { Link } from 'react-router-dom';
import { useConvexAuth, useMutation, useQuery } from 'convex/react';
import { api } from 'magical-trails-backend/convex/_generated/api';
import {
  Dialog,
  DialogPanel,
  DialogTitle,
  Transition,
  TransitionChild,
} from '@headlessui/react';
import { ExclamationTriangleIcon } from '@heroicons/react/20/solid';

import AuthForm from './AuthForm';
import Loading from '../Loading';
import { authClient } from '../../lib/authClient';
import { convexErrorMessage } from '../../lib/convexError';

const CONTACT_EMAIL = 'hello@magiczebra.co.uk';

/** /account — signed-in email, sign out, and the player's claimed trails. */
const Account: React.FC = (): JSX.Element => {
  const { isAuthenticated, isLoading } = useConvexAuth();

  if (isLoading) return <Loading />;

  return (
    <div className="start-screen flex h-screen flex-col items-center justify-center gap-6 bg-cover bg-center bg-no-repeat">
      {isAuthenticated ? <AccountInner /> : <AuthForm heading="Your account" />}
    </div>
  );
};

const AccountInner: React.FC = (): JSX.Element => {
  const session = authClient.useSession();
  const claims = useQuery(api.trailAccess.myClaims, {});
  const email = session.data?.user?.email;

  const deleteMyData = useMutation(api.trailAccess.deleteMyData);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  const handleDeleteMyData = async () => {
    setDeleting(true);
    setDeleteError(null);
    try {
      await deleteMyData({});
    } catch (error) {
      // The erasure itself failed — nothing was deleted; let them retry.
      setDeleteError(
        convexErrorMessage(
          error,
          "We couldn't delete your data. Please try again."
        )
      );
      setDeleting(false);
      setConfirmOpen(false);
      return;
    }

    // Erasure done — sign the player out. useConvexAuth flips to unauthenticated
    // and the outer <Account> re-renders the AuthForm, so nothing keeps showing
    // this now-erased player's data. If sign-out itself fails, the data is
    // ALREADY gone (don't claim deletion failed) — force a reload so the app
    // re-evaluates auth and drops the stale view.
    try {
      await authClient.signOut();
    } catch {
      window.location.reload();
    }
  };

  return (
    <div className="bg-mt-surface flex w-80 flex-col gap-5 rounded-2xl p-6 shadow-2xl">
      <div>
        <h1 className="text-mt-ink-soft text-xl font-bold tracking-tight">
          Your account
        </h1>
        {email && <p className="text-mt-ink-muted mt-1 text-sm">{email}</p>}
      </div>

      <div>
        <h2 className="text-mt-ink-soft mb-2 text-sm font-semibold">
          Your trails
        </h2>
        {claims === undefined ? (
          <p className="text-mt-ink-faint text-sm">Loading…</p>
        ) : claims.length === 0 ? (
          <p className="text-mt-ink-faint text-sm">
            No claimed trails yet. Open an invite link to add one.
          </p>
        ) : (
          <ul className="flex flex-col gap-2">
            {claims.map((claim) => {
              const firstStory = claim.storyCodes[0];
              return (
                <li key={claim.trailId}>
                  {firstStory ? (
                    <Link
                      to={`/${firstStory}`}
                      className="text-mt-primary border-mt-border-soft hover:bg-mt-primary-soft block rounded-md border px-3 py-2 text-sm font-medium"
                    >
                      {claim.trailName}
                    </Link>
                  ) : (
                    <span className="text-mt-ink-muted border-mt-border-soft block rounded-md border px-3 py-2 text-sm">
                      {claim.trailName}
                    </span>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </div>

      <div className="flex items-center justify-between">
        <Link to="/" className="text-mt-primary text-sm hover:underline">
          Back home
        </Link>
        <button
          type="button"
          onClick={() => authClient.signOut()}
          className="text-mt-ink-muted hover:text-mt-ink-soft text-sm font-semibold"
        >
          Sign out
        </button>
      </div>

      <div className="border-mt-border-soft border-t pt-4">
        {deleteError && (
          <p role="alert" className="text-mt-danger mb-2 text-xs">
            {deleteError}
          </p>
        )}
        <button
          type="button"
          onClick={() => setConfirmOpen(true)}
          className="text-mt-danger hover:text-mt-danger text-sm font-semibold"
        >
          Remove my trail access
        </button>
      </div>

      <DeleteMyDataDialog
        open={confirmOpen}
        deleting={deleting}
        onCancel={() => setConfirmOpen(false)}
        onConfirm={handleDeleteMyData}
      />
    </div>
  );
};

/**
 * Confirm dialog for removing trail access — mirrors the destructive-action
 * Dialog/Transition pattern used by TrailMenuDialog's "delete all saved
 * data" panel. Confirming calls the player-authenticated
 * `trailAccess.deleteMyData` mutation (removes every trailClaims row for
 * the signed-in player) and then signs the player out. Note this removes the
 * invite-only trail-access data (claims) only; it does NOT delete the
 * underlying login/authentication account, so the copy must not promise that.
 */
function DeleteMyDataDialog({
  open,
  deleting,
  onCancel,
  onConfirm,
}: {
  open: boolean;
  deleting: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}) {
  return (
    <Transition show={open} as={Fragment}>
      <Dialog as="div" className="relative z-10" onClose={onCancel}>
        <TransitionChild
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-gray-500/75 transition-opacity" />
        </TransitionChild>

        <div className="fixed inset-0 z-10 w-screen overflow-y-auto">
          <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
            <TransitionChild
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              <DialogPanel className="bg-mt-surface relative transform overflow-hidden rounded-lg px-4 pt-5 pb-4 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg sm:p-6">
                <div className="sm:flex sm:items-start">
                  <div className="bg-mt-danger-soft mx-auto flex h-12 w-12 shrink-0 items-center justify-center rounded-full sm:mx-0 sm:h-10 sm:w-10">
                    <ExclamationTriangleIcon
                      className="text-mt-danger h-6 w-6"
                      aria-hidden="true"
                    />
                  </div>
                  <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
                    <DialogTitle
                      as="h3"
                      className="text-mt-ink text-base leading-6 font-semibold"
                    >
                      Remove your trail access?
                    </DialogTitle>
                    <div className="mt-2">
                      <p className="text-mt-ink-muted text-sm">
                        This removes your invite-only trail-access data (your
                        claims) and signs you out. It does not delete your login
                        account or authentication records. To request full
                        account deletion, email {CONTACT_EMAIL}. This cannot be
                        undone.
                      </p>
                    </div>
                  </div>
                </div>
                <div className="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse">
                  <button
                    type="button"
                    disabled={deleting}
                    className="inline-flex w-full justify-center rounded-md bg-red-600 px-3 py-2 text-sm font-semibold text-white shadow-xs hover:bg-red-500 disabled:cursor-not-allowed disabled:opacity-60 sm:ml-3 sm:w-auto"
                    onClick={onConfirm}
                  >
                    {deleting ? 'Removing…' : 'Remove access'}
                  </button>
                  <button
                    type="button"
                    disabled={deleting}
                    className="bg-mt-surface text-mt-ink ring-mt-border hover:bg-mt-surface-2 mt-3 inline-flex w-full justify-center rounded-md px-3 py-2 text-sm font-semibold shadow-xs ring-1 ring-inset disabled:cursor-not-allowed disabled:opacity-60 sm:mt-0 sm:w-auto"
                    onClick={onCancel}
                  >
                    Cancel
                  </button>
                </div>
              </DialogPanel>
            </TransitionChild>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
}

export default Account;
